import { createRandom } from '../core/random.js';

/**
 * How a form takes over from the one before it. The weights steer *which*
 * particles move first: staggering the morph by height makes a form grow from
 * the ground up, by radius makes it ripple out from the centre, and so on. That
 * per-particle delay is what turns a crossfade into something that looks like
 * matter flowing.
 */
export interface Transition {
  readonly name: string;
  /** Stagger by a per-particle random value. */
  readonly byRandom: number;
  /** Stagger by height. Positive: bottom first. */
  readonly byHeight: number;
  /** Stagger by distance from the centre. Positive: centre first. */
  readonly byRadius: number;
  /** Stagger by angle around the centre — spirals. */
  readonly byAngle: number;
  /** Stagger sideways — curtains and wipes. */
  readonly byLateral: number;
  /** Fraction of the blend spent waiting for your turn, 0–0.9. */
  readonly spread: number;
  /** Extra turbulence at the midpoint, where the form is pure flow. */
  readonly turbulence: number;
}

const DISSOLVE: Transition = {
  name: 'dissolve',
  byRandom: 1.7,
  byHeight: 0,
  byRadius: 0,
  byAngle: 0,
  byLateral: 0,
  spread: 0.72,
  turbulence: 1,
};

export const TRANSITIONS: readonly Transition[] = [
  DISSOLVE,
  {
    name: 'rise',
    byRandom: 0.3,
    byHeight: 1.9,
    byRadius: 0,
    byAngle: 0,
    byLateral: 0,
    spread: 0.8,
    turbulence: 0.7,
  },
  {
    name: 'fall',
    byRandom: 0.3,
    byHeight: -1.9,
    byRadius: 0,
    byAngle: 0,
    byLateral: 0,
    spread: 0.8,
    turbulence: 0.7,
  },
  {
    name: 'ripple',
    byRandom: 0.25,
    byHeight: 0,
    byRadius: 1.9,
    byAngle: 0,
    byLateral: 0,
    spread: 0.78,
    turbulence: 0.85,
  },
  {
    name: 'implode',
    byRandom: 0.25,
    byHeight: 0,
    byRadius: -1.9,
    byAngle: 0,
    byLateral: 0,
    spread: 0.78,
    turbulence: 1.1,
  },
  {
    name: 'spiral',
    byRandom: 0.2,
    byHeight: 0.4,
    byRadius: 0.7,
    byAngle: 1.5,
    byLateral: 0,
    spread: 0.82,
    turbulence: 1.2,
  },
  {
    name: 'curtain',
    byRandom: 0.2,
    byHeight: 0,
    byRadius: 0,
    byAngle: 0,
    byLateral: 2,
    spread: 0.84,
    turbulence: 0.8,
  },
  {
    name: 'swarm',
    byRandom: 0.5,
    byHeight: 0,
    byRadius: 0,
    byAngle: 0,
    byLateral: 0,
    spread: 0.18,
    turbulence: 1.9,
  },
];

/** Camera behaviours. The stage smooths between them, so there are never cuts. */
export const CAMERA_MOVES = [
  'orbit',
  'push',
  'crane',
  'glide',
  'sweep',
  'hover',
  'overhead',
  'lowAngle',
  'closeUp',
  'corkscrew',
  'dutchOrbit',
] as const;
export type CameraMove = (typeof CAMERA_MOVES)[number];

/** One scheduled visit to one form. */
export interface Segment {
  readonly shape: number;
  /** Seed for this visit's layout — the same shape is never built twice alike. */
  readonly seed: number;
  /** Continuous variation knob handed to the shape. */
  readonly variant: number;
  readonly holdSeconds: number;
  readonly blendSeconds: number;
  readonly transition: Transition;
  readonly camera: CameraMove;
  /** Slow turn applied to the whole form, radians per second. */
  readonly spin: number;
  /** Palette rotation in [-0.5, 0.5] — the same form in a different light. */
  readonly hueShift: number;
  /** Overall brightness bias for this visit, around 1. */
  readonly glow: number;
}

const MIN_HOLD_S = 15;
const HOLD_RANGE_S = 24;
const MIN_BLEND_S = 4;
const BLEND_RANGE_S = 4.5;

/**
 * Decides what to show next, forever. Shapes come out of a shuffled bag so
 * every form is seen once before any repeats, and each visit re-rolls its seed,
 * variant, transition, camera, palette rotation and duration — so a form that
 * does come round again arrives differently built, differently lit and
 * differently framed.
 */
export class Director {
  private readonly random: () => number;
  private readonly shapeCount: number;
  private bag: number[] = [];
  private lastShape = -1;

  constructor(shapeCount: number, seed: number) {
    this.shapeCount = Math.max(1, shapeCount);
    this.random = createRandom(seed);
  }

  public next(): Segment {
    const shape = this.drawShape();
    this.lastShape = shape;
    const isBrisk = this.random() < 0.14;
    const isLanguid = this.random() < 0.16;
    return {
      shape,
      seed: Math.floor(this.random() * 0x7f_ff_ff_ff),
      variant: this.random(),
      holdSeconds: isBrisk ? 8 + this.random() * 4 : MIN_HOLD_S + this.random() * HOLD_RANGE_S,
      blendSeconds: isLanguid
        ? 10 + this.random() * 5
        : MIN_BLEND_S + this.random() * BLEND_RANGE_S,
      transition: this.drawTransition(),
      camera: CAMERA_MOVES[Math.floor(this.random() * CAMERA_MOVES.length)] ?? 'orbit',
      spin: (this.random() - 0.5) * 0.11,
      hueShift: (this.random() - 0.5) * 0.22,
      glow: 0.85 + this.random() * 0.35,
    };
  }

  private drawTransition(): Transition {
    const pick = Math.floor(this.random() * TRANSITIONS.length);
    return TRANSITIONS[pick] ?? DISSOLVE;
  }

  private drawShape(): number {
    if (this.bag.length === 0) {
      this.refill();
    }
    return this.bag.pop() ?? 0;
  }

  private refill(): void {
    const indices: number[] = [];
    for (let i = 0; i < this.shapeCount; i += 1) {
      indices.push(i);
    }
    for (let i = indices.length - 1; i > 0; i -= 1) {
      const j = Math.floor(this.random() * (i + 1));
      const a = indices[i];
      const b = indices[j];
      if (a !== undefined && b !== undefined) {
        indices[i] = b;
        indices[j] = a;
      }
    }
    // `pop()` takes from the end, so the last entry must not repeat the form
    // that is on screen right now.
    const last = indices.at(-1);
    if (indices.length > 1 && last === this.lastShape) {
      const swap = indices[0];
      if (swap !== undefined) {
        indices[indices.length - 1] = swap;
        indices[0] = last;
      }
    }
    this.bag = indices;
  }
}

export type TimelinePhase = 'hold' | 'blend';
export type TimelineEvent = 'none' | 'blend-started' | 'arrived';

/**
 * The clock that walks the director's segments. It holds a form, then blends
 * into the next one — but only once the caller says the next form has finished
 * baking, so a slow bake stretches the hold instead of stuttering the morph.
 */
export class Timeline {
  private readonly director: Director;
  private showing: Segment;
  private next: Segment;
  private currentPhase: TimelinePhase = 'hold';
  private elapsed = 0;
  private blendProgress = 0;

  constructor(director: Director) {
    this.director = director;
    this.showing = director.next();
    this.next = director.next();
  }

  public get current(): Segment {
    return this.showing;
  }

  public get incoming(): Segment {
    return this.next;
  }

  public get phase(): TimelinePhase {
    return this.currentPhase;
  }

  /** Blend position in [0, 1]: 0 while holding, 1 the instant before arrival. */
  public get progress(): number {
    return this.blendProgress;
  }

  public update(deltaSeconds: number, isIncomingReady: boolean): TimelineEvent {
    this.elapsed += deltaSeconds;
    if (this.currentPhase === 'hold') {
      if (isIncomingReady && this.elapsed >= this.showing.holdSeconds) {
        this.currentPhase = 'blend';
        this.elapsed = 0;
        this.blendProgress = 0;
        return 'blend-started';
      }
      return 'none';
    }
    this.blendProgress = Math.min(1, this.elapsed / Math.max(0.5, this.next.blendSeconds));
    if (this.blendProgress >= 1) {
      this.showing = this.next;
      this.next = this.director.next();
      this.currentPhase = 'hold';
      this.elapsed = 0;
      this.blendProgress = 0;
      return 'arrived';
    }
    return 'none';
  }
}
